import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Bot, Phone, Sliders, MessageSquare, Clock, Zap, Save, Play, Settings2, Info, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

const VoiceAgentConfig = () => {
  const [selectedAgent, setSelectedAgent] = useState('land');
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const agents = [
    { id: 'land', name: 'Land Scout Voice Agent', type: 'Land Acquisition', color: 'bg-green-500' },
    { id: 'wholesale', name: 'Wholesale Voice Agent', type: 'Distressed SFH', color: 'bg-blue-500' },
    { id: 'section8', name: 'Section 8 Voice Agent', type: 'Creative Finance', color: 'bg-orange-500' },
    { id: 'multifamily', name: 'Multifamily Voice Agent', type: 'Broker Outreach', color: 'bg-purple-500' }
  ];

  useEffect(() => {
    fetchConfig();
  }, [selectedAgent]);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/voice-ai/configs/${selectedAgent}`);
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
      } else {
        // Create default config with comprehensive scripts
        const defaultScripts = {
          land: {
            opening: "Hello, this is {bot_name} calling from REIPR. Am I speaking with {seller_name}? *pause* Great! I'm reaching out because we're actively acquiring land in your area, and I wanted to see if you'd be interested in a cash offer for your property at {address}.",
            qualification: "I'd love to learn a bit more about your land. *pause* First, can you tell me about access to the property? Is there road frontage or an easement? *pause* And what about utilities - are water, power, or sewer available nearby? *pause* Finally, what's prompting you to consider selling at this time?",
            objection: "I completely understand your concern about the price. *pause* Let me explain how we arrive at our offers. We look at recent comparable sales, factor in development costs, and assess utility access. *pause* Our goal is to make this a quick, hassle-free transaction with cash. *pause* Would you be open to discussing the numbers in more detail?"
          },
          wholesale: {
            opening: "Hi {seller_name}, this is {bot_name} from REIPR. How are you today? *pause* I'm calling because we buy houses in your neighborhood, and I wanted to see if you'd consider a cash offer for your property at {address}. We can close quickly and buy as-is, no repairs needed.",
            qualification: "I'd like to ask you a few quick questions to see if we can help. *pause* First, can you tell me about the overall condition of the house? Any major repairs needed? *pause* And what's your timeline - are you looking to sell soon or just exploring options? *pause* Finally, is there a particular reason you're thinking about selling?",
            objection: "I hear you, and I appreciate your honesty about the price. *pause* Here's the thing - we're not retail buyers. We buy houses as-is, handle all the paperwork, and close in as little as 7 days with cash. *pause* No agent commissions, no repairs, no hassle. *pause* When you factor in those costs and convenience, our offer often makes a lot of sense. Would you like me to break down the numbers?"
          },
          section8: {
            opening: "Hello {seller_name}, this is {bot_name} with REIPR. *pause* I'm reaching out because we specialize in creating opportunities for landlords who are tired of the headaches. We buy properties and keep them as Section 8 rentals. *pause* I wanted to see if you'd be interested in discussing your property at {address}.",
            qualification: "Let me ask you a few questions to see if this could be a fit. *pause* First, what's the current condition of the property? *pause* Are you currently renting it out, or is it vacant? *pause* And if you are renting, what's the monthly rent? *pause* Finally, what's been your biggest challenge as a landlord?",
            objection: "I totally understand where you're coming from. *pause* The reason we can make this work is we're keeping it as a rental, not flipping it. *pause* We can offer seller financing terms that give you monthly income without the landlord headaches. *pause* You'd get a down payment now, monthly payments, and no more tenant calls. *pause* Does that sound like something worth exploring?"
          },
          multifamily: {
            opening: "Good morning {seller_name}, this is {bot_name} calling from REIPR. *pause* We're actively acquiring multifamily properties in your market, and I wanted to reach out about your property at {address}. *pause* Do you have a few minutes to discuss a potential acquisition?",
            qualification: "I'd like to understand your property better. *pause* First, can you tell me the unit count and mix? *pause* What's the current occupancy rate? *pause* And do you have a trailing twelve month profit and loss statement you could share? *pause* Finally, what's driving your interest in selling at this time?",
            objection: "I appreciate you sharing your thoughts on valuation. *pause* Let me walk you through how we underwrote this. We're looking at your NOI, applying a market cap rate for the submarket, and factoring in deferred maintenance. *pause* We're also considering a faster close and certainty of execution. *pause* Would it be helpful if I sent over our detailed offer analysis so you can review the numbers?"
          }
        };

        const scripts = defaultScripts[selectedAgent] || defaultScripts.land;
        
        setConfig({
          id: selectedAgent,
          name: agents.find(a => a.id === selectedAgent)?.name,
          max_exchanges: 10,
          max_duration_seconds: 600,
          reply_timeout_seconds: 20,
          escalate_after_unanswered: 3,
          message_delay_ms: 1000,
          use_randomized_pauses: true,
          typing_delay_ms: 500,
          engagement_check_enabled: true,
          engagement_check_after_seconds: 30,
          max_engagement_checks: 2,
          tone: 'professional',
          message_length: 'medium',
          use_response_variability: true,
          use_disfluencies: false,
          opening_script: scripts.opening,
          qualification_script: scripts.qualification,
          objection_handling_script: scripts.objection,
          // Voice settings
          voice_gender: 'male',
          voice_style: 'professional',
          speaking_rate: 1.0,
          pitch: 0,
          energy_level: 60,
          accent: 'american_neutral',
          modulation: 50,
          // Advanced controls
          backchannel_enabled: true,
          backchannel_probability: 0.3,
          max_idle_duration: 10,
          idle_reminder_count: 2,
          initial_pause_ms: 800,
          pause_range_min_ms: 1000,
          pause_range_max_ms: 3000,
          interruption_sensitivity: 'medium',
          speech_stop_sensitivity: 'medium',
          max_tokens: 1000
        });
      }
    } catch (err) {
      console.error('Failed to fetch config:', err);
    }
    setLoading(false);
  };

  const saveConfig = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/voice-ai/configs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });
      if (res.ok) {
        toast.success('Voice agent configuration saved successfully!');
      } else {
        toast.error('Failed to save configuration');
      }
    } catch (err) {
      console.error('Failed to save config:', err);
      toast.error('Error saving configuration');
    }
  };

  const updateConfig = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const TooltipWrapper = ({ children, text }) => (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center gap-1">
            {children}
            <HelpCircle className="h-3 w-3 text-muted-foreground" />
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p className="text-sm">{text}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Settings2 className="h-8 w-8 text-primary" />
                Voice Agent Configuration
              </h1>
              <p className="text-muted-foreground mt-1">
                Configure voice identity, conversation behavior, pacing, and scripts
              </p>
            </div>
            <Button size="lg" onClick={saveConfig}>
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-12 gap-6">
          {/* Agent Selector */}
          <div className="col-span-3">
            <Card>
              <CardHeader>
                <CardTitle>Select Agent</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {agents.map(agent => (
                  <button
                    key={agent.id}
                    onClick={() => setSelectedAgent(agent.id)}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                      selectedAgent === agent.id
                        ? 'border-primary bg-primary/10'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`h-10 w-10 rounded-full ${agent.color} flex items-center justify-center`}>
                        <Phone className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <div className="font-semibold text-sm">{agent.name}</div>
                        <div className="text-xs text-muted-foreground">{agent.type}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Configuration Panel */}
          <div className="col-span-9">
            <Tabs defaultValue="voice" className="space-y-6">
              <TabsList className="grid w-full grid-cols-5">
                <TabsTrigger value="voice">Voice Identity</TabsTrigger>
                <TabsTrigger value="behavior">Conversation</TabsTrigger>
                <TabsTrigger value="advanced">Advanced</TabsTrigger>
                <TabsTrigger value="scripts">Scripts</TabsTrigger>
                <TabsTrigger value="routing">Routing</TabsTrigger>
              </TabsList>

              {/* VOICE IDENTITY TAB */}
              <TabsContent value="voice" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Voice Identity Controls</CardTitle>
                    <CardDescription>Configure how your voice agent sounds</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <TooltipWrapper text="Choose the gender presentation of the voice">
                        <Label>Voice Gender</Label>
                      </TooltipWrapper>
                      <RadioGroup 
                        value={config?.voice_gender || 'male'}
                        onValueChange={(value) => updateConfig('voice_gender', value)}
                        className="flex gap-4 mt-3"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="male" id="male" />
                          <Label htmlFor="male">Male</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="female" id="female" />
                          <Label htmlFor="female">Female</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="neutral" id="neutral" />
                          <Label htmlFor="neutral">Neutral</Label>
                        </div>
                      </RadioGroup>
                    </div>

                    <div>
                      <TooltipWrapper text="Select the overall tone and speaking style">
                        <Label>Voice Tone</Label>
                      </TooltipWrapper>
                      <Select 
                        value={config?.voice_style || 'professional'}
                        onValueChange={(value) => updateConfig('voice_style', value)}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="casual">Casual</SelectItem>
                          <SelectItem value="neutral">Neutral</SelectItem>
                          <SelectItem value="professional">Professional</SelectItem>
                          <SelectItem value="formal">Formal</SelectItem>
                          <SelectItem value="excited">Excited</SelectItem>
                          <SelectItem value="calm">Calm</SelectItem>
                          <SelectItem value="empathetic">Empathetic</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <TooltipWrapper text="Adjust how fast the agent speaks (0.5x = slow, 1.5x = fast)">
                        <Label>Speaking Rate</Label>
                      </TooltipWrapper>
                      <div className="flex items-center gap-4 mt-4">
                        <Slider 
                          value={[config?.speaking_rate || 1.0]}
                          onValueChange={([value]) => updateConfig('speaking_rate', value)}
                          min={0.5}
                          max={1.5}
                          step={0.1}
                          className="flex-1"
                        />
                        <span className="text-sm font-mono w-12">{config?.speaking_rate?.toFixed(1) || '1.0'}x</span>
                      </div>
                    </div>

                    <div>
                      <TooltipWrapper text="Adjust voice pitch (-10 = deeper, +10 = higher)">
                        <Label>Pitch</Label>
                      </TooltipWrapper>
                      <div className="flex items-center gap-4 mt-4">
                        <Slider 
                          value={[config?.pitch || 0]}
                          onValueChange={([value]) => updateConfig('pitch', value)}
                          min={-10}
                          max={10}
                          step={1}
                          className="flex-1"
                        />
                        <span className="text-sm font-mono w-12">{config?.pitch || 0}</span>
                      </div>
                    </div>

                    <div>
                      <TooltipWrapper text="Energy and enthusiasm level in voice delivery">
                        <Label>Energy Level</Label>
                      </TooltipWrapper>
                      <div className="flex items-center gap-4 mt-4">
                        <Slider 
                          value={[config?.energy_level || 60]}
                          onValueChange={([value]) => updateConfig('energy_level', value)}
                          min={0}
                          max={100}
                          className="flex-1"
                        />
                        <span className="text-sm font-mono w-12">{config?.energy_level || 60}%</span>
                      </div>
                    </div>

                    <div>
                      <TooltipWrapper text="Voice modulation and variation in speech patterns">
                        <Label>Modulation (Voice Variation)</Label>
                      </TooltipWrapper>
                      <div className="flex items-center gap-4 mt-4">
                        <Slider 
                          value={[config?.modulation || 50]}
                          onValueChange={([value]) => updateConfig('modulation', value)}
                          min={0}
                          max={100}
                          className="flex-1"
                        />
                        <span className="text-sm font-mono w-12">{config?.modulation || 50}%</span>
                      </div>
                    </div>

                    <div>
                      <TooltipWrapper text="Regional accent for voice delivery">
                        <Label>Accent</Label>
                      </TooltipWrapper>
                      <Select 
                        value={config?.accent || 'american_neutral'}
                        onValueChange={(value) => updateConfig('accent', value)}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="american_neutral">American - Neutral Corporate</SelectItem>
                          <SelectItem value="american_southern">American - Southern</SelectItem>
                          <SelectItem value="american_northeast">American - Northeast</SelectItem>
                          <SelectItem value="british">British</SelectItem>
                          <SelectItem value="hispanic">Hispanic American</SelectItem>
                          <SelectItem value="canadian">Canadian</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="pt-4 border-t">
                      <Button variant="outline" className="w-full">
                        <Play className="h-4 w-4 mr-2" />
                        Preview Voice Sample
                      </Button>
                      <Button variant="outline" className="w-full mt-2">
                        📁 Upload Custom Voice (Clone)
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* CONVERSATION BEHAVIOR TAB */}
              <TabsContent value="behavior" className="space-y-6">
                {/* Conversation Length */}
                <Card>
                  <CardHeader>
                    <CardTitle>Conversation Length Controls</CardTitle>
                    <CardDescription>Set limits for conversation flow</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <TooltipWrapper text="Maximum back-and-forth exchanges before escalating or ending call">
                        <Label>Max Number of Exchanges</Label>
                      </TooltipWrapper>
                      <Input 
                        type="number" 
                        value={config?.max_exchanges || 10}
                        onChange={(e) => updateConfig('max_exchanges', parseInt(e.target.value))}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <TooltipWrapper text="Maximum call duration before automatic termination">
                        <Label>Max Call Duration (seconds)</Label>
                      </TooltipWrapper>
                      <Input 
                        type="number" 
                        value={config?.max_duration_seconds || 600}
                        onChange={(e) => updateConfig('max_duration_seconds', parseInt(e.target.value))}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <TooltipWrapper text="How long to wait for seller response before prompting">
                        <Label>Reply Timeout (seconds)</Label>
                      </TooltipWrapper>
                      <Input 
                        type="number" 
                        value={config?.reply_timeout_seconds || 20}
                        onChange={(e) => updateConfig('reply_timeout_seconds', parseInt(e.target.value))}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <TooltipWrapper text="Number of unanswered questions before escalating to human">
                        <Label>Escalate After Unanswered</Label>
                      </TooltipWrapper>
                      <Input 
                        type="number" 
                        value={config?.escalate_after_unanswered || 3}
                        onChange={(e) => updateConfig('escalate_after_unanswered', parseInt(e.target.value))}
                        className="mt-2"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Pause & Timing */}
                <Card>
                  <CardHeader>
                    <CardTitle>Pause & Timing Controls</CardTitle>
                    <CardDescription>Natural conversation pacing</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <TooltipWrapper text="Initial pause before agent starts speaking (in milliseconds)">
                        <Label>Initial Pause Before Speaking</Label>
                      </TooltipWrapper>
                      <div className="flex items-center gap-4 mt-4">
                        <Slider 
                          value={[config?.initial_pause_ms || 800]}
                          onValueChange={([value]) => updateConfig('initial_pause_ms', value)}
                          min={0}
                          max={3000}
                          step={100}
                          className="flex-1"
                        />
                        <span className="text-sm font-mono w-16">{config?.initial_pause_ms || 800}ms</span>
                      </div>
                    </div>

                    <div>
                      <TooltipWrapper text="Base delay between message segments">
                        <Label>Message Delay</Label>
                      </TooltipWrapper>
                      <div className="flex items-center gap-4 mt-4">
                        <Slider 
                          value={[config?.message_delay_ms || 1000]}
                          onValueChange={([value]) => updateConfig('message_delay_ms', value)}
                          min={500}
                          max={3000}
                          step={100}
                          className="flex-1"
                        />
                        <span className="text-sm font-mono w-16">{config?.message_delay_ms || 1000}ms</span>
                      </div>
                    </div>

                    <div>
                      <TooltipWrapper text="Range for randomized pauses to sound more natural">
                        <Label>Randomized Pause Range</Label>
                      </TooltipWrapper>
                      <div className="grid grid-cols-2 gap-4 mt-2">
                        <Input 
                          type="number"
                          placeholder="Min (ms)"
                          value={config?.pause_range_min_ms || 1000}
                          onChange={(e) => updateConfig('pause_range_min_ms', parseInt(e.target.value))}
                        />
                        <Input 
                          type="number"
                          placeholder="Max (ms)"
                          value={config?.pause_range_max_ms || 3000}
                          onChange={(e) => updateConfig('pause_range_max_ms', parseInt(e.target.value))}
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <TooltipWrapper text="Adds natural variation to response timing">
                          <Label>Use Randomized Pauses</Label>
                        </TooltipWrapper>
                      </div>
                      <Switch 
                        checked={config?.use_randomized_pauses || false}
                        onCheckedChange={(checked) => updateConfig('use_randomized_pauses', checked)}
                      />
                    </div>

                    <div>
                      <TooltipWrapper text="How quickly agent stops speaking when interrupted">
                        <Label>Interruption Sensitivity</Label>
                      </TooltipWrapper>
                      <Select 
                        value={config?.interruption_sensitivity || 'medium'}
                        onValueChange={(value) => updateConfig('interruption_sensitivity', value)}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low - Lets seller finish</SelectItem>
                          <SelectItem value="medium">Medium - Balanced</SelectItem>
                          <SelectItem value="high">High - Stops immediately</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>

                {/* Human-Like Behavior */}
                <Card>
                  <CardHeader>
                    <CardTitle>Human-Like Behavior</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <TooltipWrapper text="Adds 'um', 'uh', 'so' for natural speech">
                          <Label>Use Mild Disfluencies</Label>
                        </TooltipWrapper>
                      </div>
                      <Switch 
                        checked={config?.use_disfluencies || false}
                        onCheckedChange={(checked) => updateConfig('use_disfluencies', checked)}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <TooltipWrapper text="Varies response phrasing to avoid sounding robotic">
                          <Label>Response Variability</Label>
                        </TooltipWrapper>
                      </div>
                      <Switch 
                        checked={config?.use_response_variability || false}
                        onCheckedChange={(checked) => updateConfig('use_response_variability', checked)}
                      />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* ADVANCED TAB */}
              <TabsContent value="advanced" className="space-y-6">
                {/* Backchanneling */}
                <Card>
                  <CardHeader>
                    <CardTitle>Backchanneling</CardTitle>
                    <CardDescription>Verbal acknowledgments while listening</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <TooltipWrapper text="Enable 'mm-hmm', 'okay', 'right' responses while seller talks">
                          <Label>Enable Backchanneling</Label>
                        </TooltipWrapper>
                      </div>
                      <Switch 
                        checked={config?.backchannel_enabled || false}
                        onCheckedChange={(checked) => updateConfig('backchannel_enabled', checked)}
                      />
                    </div>

                    {config?.backchannel_enabled && (
                      <div>
                        <TooltipWrapper text="How often agent interjects acknowledgments (0 = rarely, 1 = frequently)">
                          <Label>Backchannel Probability</Label>
                        </TooltipWrapper>
                        <div className="flex items-center gap-4 mt-4">
                          <Slider 
                            value={[config?.backchannel_probability || 0.3]}
                            onValueChange={([value]) => updateConfig('backchannel_probability', value)}
                            min={0}
                            max={1}
                            step={0.1}
                            className="flex-1"
                          />
                          <span className="text-sm font-mono w-12">{((config?.backchannel_probability || 0.3) * 100).toFixed(0)}%</span>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Idle Time Management */}
                <Card>
                  <CardHeader>
                    <CardTitle>Idle Time Management</CardTitle>
                    <CardDescription>"Are you still there?" logic</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <TooltipWrapper text="Check if seller is still engaged during long silences">
                          <Label>Enable Engagement Checks</Label>
                        </TooltipWrapper>
                      </div>
                      <Switch 
                        checked={config?.engagement_check_enabled || false}
                        onCheckedChange={(checked) => updateConfig('engagement_check_enabled', checked)}
                      />
                    </div>

                    {config?.engagement_check_enabled && (
                      <>
                        <div>
                          <TooltipWrapper text="Seconds of silence before asking 'are you still there?'">
                            <Label>Check After Idle Duration (seconds)</Label>
                          </TooltipWrapper>
                          <Input 
                            type="number" 
                            value={config?.engagement_check_after_seconds || 30}
                            onChange={(e) => updateConfig('engagement_check_after_seconds', parseInt(e.target.value))}
                            className="mt-2"
                          />
                        </div>

                        <div>
                          <TooltipWrapper text="Maximum number of engagement check attempts">
                            <Label>Max Engagement Checks</Label>
                          </TooltipWrapper>
                          <Input 
                            type="number" 
                            value={config?.max_engagement_checks || 2}
                            onChange={(e) => updateConfig('max_engagement_checks', parseInt(e.target.value))}
                            className="mt-2"
                          />
                        </div>

                        <div>
                          <Label>Reminder Message</Label>
                          <Input 
                            placeholder="Hello? Are you still there?"
                            className="mt-2"
                          />
                        </div>

                        <div>
                          <Label>Timeout Message (Before Hangup)</Label>
                          <Textarea 
                            placeholder="I haven't heard from you, so I'll let you go. Feel free to call me back anytime!"
                            rows={3}
                            className="mt-2"
                          />
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* Speech Stop Sensitivity */}
                <Card>
                  <CardHeader>
                    <CardTitle>Speech Stop Sensitivity</CardTitle>
                    <CardDescription>How quickly agent stops when interrupted</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <Select 
                        value={config?.speech_stop_sensitivity || 'medium'}
                        onValueChange={(value) => updateConfig('speech_stop_sensitivity', value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low - Finishes thought</SelectItem>
                          <SelectItem value="medium">Medium - Balanced</SelectItem>
                          <SelectItem value="high">High - Stops instantly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>

                {/* Max Tokens */}
                <Card>
                  <CardHeader>
                    <CardTitle>Token & Duration Limits</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <TooltipWrapper text="Maximum tokens (words) per response">
                        <Label>Max Tokens Per Response</Label>
                      </TooltipWrapper>
                      <Input 
                        type="number" 
                        value={config?.max_tokens || 1000}
                        onChange={(e) => updateConfig('max_tokens', parseInt(e.target.value))}
                        className="mt-2"
                      />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* SCRIPTS TAB */}
              <TabsContent value="scripts" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Voice Conversation Scripts</CardTitle>
                    <CardDescription>
                      Use tokens: {'{bot_name}'}, {'{seller_name}'}, {'{address}'}, {'{offer_price}'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Explainer Box */}
                    <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <div className="text-blue-600 dark:text-blue-400 mt-1">ℹ️</div>
                        <div className="text-sm">
                          <div className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
                            Voice Scripts vs. Legion Bot Scripts
                          </div>
                          <div className="text-blue-800 dark:text-blue-200 space-y-1">
                            <p><strong>🎙️ Voice Scripts (here):</strong> Control HOW the agent speaks - tone, pacing, and conversational flow for phone calls</p>
                            <p><strong>🧠 Legion Bot Scripts:</strong> Control WHAT the agent does - business logic, qualification rules, and multi-channel messaging (SMS/Email)</p>
                            <p className="mt-2 pt-2 border-t border-blue-200 dark:border-blue-700">
                              <strong>💡 Best Practice:</strong> Voice scripts should focus on natural phone conversation. Business logic stays in Legion Bots.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <Label>Opening Script (Phone Calls Only)</Label>
                      <p className="text-xs text-muted-foreground mb-2">
                        First words spoken when agent answers or makes a call. Use *pause* for natural breaks.
                      </p>
                      <Textarea 
                        value={config?.opening_script || ''}
                        onChange={(e) => updateConfig('opening_script', e.target.value)}
                        rows={4}
                        className="mt-2 font-mono text-sm"
                      />
                    </div>
                    <div>
                      <Label>Qualification Script (Voice Delivery)</Label>
                      <p className="text-xs text-muted-foreground mb-2">
                        How to ask qualification questions over the phone (business logic is in Legion Bot)
                      </p>
                      <Textarea 
                        value={config?.qualification_script || ''}
                        onChange={(e) => updateConfig('qualification_script', e.target.value)}
                        rows={6}
                        className="mt-2 font-mono text-sm"
                      />
                    </div>
                    <div>
                      <Label>Objection Handling Script (Phone Tone)</Label>
                      <p className="text-xs text-muted-foreground mb-2">
                        How to respond to seller concerns conversationally
                      </p>
                      <Textarea 
                        value={config?.objection_handling_script || ''}
                        onChange={(e) => updateConfig('objection_handling_script', e.target.value)}
                        rows={6}
                        className="mt-2 font-mono text-sm"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline">
                        <Play className="h-4 w-4 mr-2" />
                        Preview Voice
                      </Button>
                      <Button variant="outline">
                        📋 View Legion Bot Scripts
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Custom Vocabulary */}
                <Card>
                  <CardHeader>
                    <CardTitle>Custom Vocabulary & Pronunciations</CardTitle>
                    <CardDescription>Add domain-specific terms for accurate pronunciation</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label>Property Addresses</Label>
                      <Textarea 
                        placeholder="123 Main Street, Irvine, California"
                        rows={2}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label>Industry Terms</Label>
                      <Textarea 
                        placeholder="ARV, BRRRR, NOI, Cap Rate, DSCR, T12, FMR"
                        rows={2}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label>Names & Special Terms</Label>
                      <Textarea 
                        placeholder="KlearWater, REIPR, Zillow, Redfin"
                        rows={2}
                        className="mt-2"
                      />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* ROUTING TAB */}
              <TabsContent value="routing" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Geography Controls</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <Label>County Whitelist (comma separated)</Label>
                      <Input className="mt-2" placeholder="Orange, Los Angeles, San Diego" />
                    </div>
                    <div>
                      <Label>County Blacklist</Label>
                      <Input className="mt-2" placeholder="Riverside, San Bernardino" />
                    </div>
                    <div>
                      <Label>ZIP Code Include</Label>
                      <Input className="mt-2" placeholder="92618, 92602, 90210" />
                    </div>
                    <div>
                      <Label>ZIP Code Exclude</Label>
                      <Input className="mt-2" placeholder="90001, 90002" />
                    </div>
                    <div>
                      <Label>Search Radius (miles)</Label>
                      <Input type="number" defaultValue={50} className="mt-2" />
                    </div>
                  </CardContent>
                </Card>

                {/* Frequency Controls */}
                <Card>
                  <CardHeader>
                    <CardTitle>Frequency Controls</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <TooltipWrapper text="Maximum outbound call attempts per lead per day">
                        <Label>Max Daily Attempts Per Lead</Label>
                      </TooltipWrapper>
                      <Input type="number" defaultValue={3} className="mt-2" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Quiet Hours Start</Label>
                        <Input type="time" defaultValue="21:00" className="mt-2" />
                      </div>
                      <div>
                        <Label>Quiet Hours End</Label>
                        <Input type="time" defaultValue="09:00" className="mt-2" />
                      </div>
                    </div>
                    <div>
                      <Label>Weekly Follow-up Cap</Label>
                      <Input type="number" defaultValue={5} className="mt-2" />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoiceAgentConfig;